import { useMemo, useSyncExternalStore } from "react";
import { create } from "zustand";
import { fetchProblemDocument, fetchProblemIndex } from "./api";
import {
  categoryOf,
  searchProblems,
  type Filter,
  type ProblemDocument,
  type ProblemType,
  type RankedProblem,
} from "./errors";

export type LoadStatus = "idle" | "loading" | "ready" | "error";

interface ErrorsState {
  index: ProblemType[] | null;
  indexStatus: LoadStatus;
  // Per-code document cache. Present key = fetched (value may be null when the
  // catalog has no such code). Cached for the app instance's lifetime.
  docs: Record<string, ProblemDocument | null>;
  docStatus: Record<string, LoadStatus>;
  query: string;
  filter: Filter;
  loadIndex: () => Promise<void>;
  loadDocument: (code: string) => Promise<void>;
  setQuery: (query: string) => void;
  setFilter: (filter: Filter) => void;
  clearFilters: () => void;
}

export const useErrorsStore = create<ErrorsState>((set, get) => ({
  index: null,
  indexStatus: "idle",
  docs: {},
  docStatus: {},
  query: "",
  filter: "All",

  loadIndex: async () => {
    const { indexStatus } = get();
    // Cached for the session — only (re)fetch when idle or after an error.
    if (indexStatus === "loading" || indexStatus === "ready") return;
    set({ indexStatus: "loading" });
    try {
      const index = await fetchProblemIndex();
      set({ index, indexStatus: "ready" });
    } catch {
      set({ indexStatus: "error" });
    }
  },

  loadDocument: async (code) => {
    const status = get().docStatus[code];
    if (status === "loading" || status === "ready") return;
    set((state) => ({ docStatus: { ...state.docStatus, [code]: "loading" } }));
    try {
      const doc = await fetchProblemDocument(code);
      set((state) => ({
        docs: { ...state.docs, [code]: doc },
        docStatus: { ...state.docStatus, [code]: "ready" },
      }));
    } catch {
      set((state) => ({ docStatus: { ...state.docStatus, [code]: "error" } }));
    }
  },

  setQuery: (query) => set({ query }),
  setFilter: (filter) => set({ filter }),
  clearFilters: () => set({ query: "", filter: "All" }),
}));

// Ranked list derived from the cached index + current query/filter. Memoized so
// the fzf pass only reruns when an input actually changes.
export function useRankedProblems(): RankedProblem[] {
  const index = useErrorsStore((state) => state.index);
  const query = useErrorsStore((state) => state.query);
  const filter = useErrorsStore((state) => state.filter);
  return useMemo(() => {
    const entries = index ?? [];
    let base = entries;
    if (filter === "Retryable") {
      base = entries.filter((entry) => entry.retryable);
    } else if (filter !== "All") {
      base = entries.filter((entry) => categoryOf(entry.code) === filter);
    }
    return searchProblems(base, query);
  }, [index, query, filter]);
}

// Selection lives in the URL hash (deep-linkable, survives back/forward). Reads
// go through useSyncExternalStore so any component re-renders on change.
function readHash(): string {
  return decodeURIComponent(window.location.hash.replace(/^#/, ""));
}

function subscribeHash(onChange: () => void): () => void {
  window.addEventListener("hashchange", onChange);
  return () => window.removeEventListener("hashchange", onChange);
}

export function useSelectedCode(): string | null {
  const hash = useSyncExternalStore(subscribeHash, readHash, () => "");
  return hash || null;
}

export function selectCode(code: string): void {
  window.location.hash = encodeURIComponent(code);
  window.dispatchEvent(new Event("hashchange"));
}

export function clearSelection(): void {
  history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search,
  );
  window.dispatchEvent(new Event("hashchange"));
}
